import { useEffect, useState } from 'react'
import { loadMyUnpublished, discardPost, type UnpublishedPost } from '../lib/feed'
import { messageOf } from '../lib/errors'
import { useHubLang } from '../lib/i18n'
import { COMPOSE } from '../lib/hubCopy'

/**
 * Your drafts, to pick one. FED-35.
 *
 * ── IT REPLACES THE SIDEBAR WIDGET, AND THE WIDGET'S OWN REASON IS WHY ──────
 *
 * `MyPosts` was a rail card that appeared only when you had something
 * unpublished — deliberately absent rather than empty, because "a permanent
 * 'You have no drafts' card in the sidebar is furniture that teaches everyone
 * to ignore that spot". That argument was right and it is exactly what kills
 * the card now: a surface nobody sees until they happen to have a draft is a
 * surface nobody looks for. A button that is always there, opening a list that
 * says what is in it, is the honest version. SHL-16 removes the widget;
 * this is where its contents went.
 *
 * ── A BLOCKED POST IS LISTED AND NOT OPENABLE ───────────────────────────────
 *
 * Both halves matter. It is LISTED because 028 stored blocks precisely so
 * there would be something to appeal against, and until 12 Sep an author could
 * not read the reason their own post was stopped. It is not OPENABLE because
 * 028 also made the release a moderator's act: `edit_post` refuses a blocked
 * row outright, so an Open button here would be a control that always fails,
 * which is worse than no control (rule 4).
 *
 * The author can still delete it. Throwing away your own stopped post is not
 * the decision that was taken away from you.
 */
export function DraftPicker({
  onPick,
  onClose,
}: {
  /** Hand the chosen draft up; the route opens the post dialog with it. */
  onPick: (draft: UnpublishedPost) => void
  onClose: () => void
}) {
  const t = COMPOSE[useHubLang()]
  const [items, setItems] = useState<UnpublishedPost[] | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    loadMyUnpublished()
      .then((r) => live && setItems(r))
      // A failure here is an empty list with an error above it, never a broken
      // dialog — the same posture the rail card had.
      .catch((e) => {
        if (!live) return
        setItems([])
        setError(messageOf(e))
      })
    return () => {
      live = false
    }
  }, [])

  async function throwAway(item: UnpublishedPost) {
    /* The word on the button is read from firstPublishedAt, so it says what
       will actually happen BEFORE the click — 041's rule. A draft nobody ever
       saw is really deleted; anything that went out is soft-deleted and keeps
       its record. Do NOT re-derive this from `state`: after 040 an item can
       sit at state='draft' having been published for a fortnight (rule 10). */
    const forever = item.firstPublishedAt === null
    const ok = window.confirm(
      forever
        ? t.confirmDelete
        : t.confirmRemove,
    )
    if (!ok) return

    setBusy(item.contentId)
    setError(null)
    try {
      await discardPost(item.contentId)
      setItems(await loadMyUnpublished())
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(null)
    }
  }

  if (items === null) {
    return (
      <p className="empty" aria-busy="true">
        {t.lookingForDrafts}
      </p>
    )
  }

  return (
    <>
      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}

      {items.length === 0 ? (
        /* An empty list here is FINE, unlike in the rail: somebody pressed a
           button labelled "Your drafts" and is owed an answer to the question
           they asked. Rule 12's spirit — say the true thing about the world
           rather than showing nothing. */
        <p className="empty">
          {t.noDrafts}
        </p>
      ) : (
        <ul className="mydrafts">
          {items.map((item) => {
            const isBlocked = item.moderationStatus === 'blocked' || item.blockReason !== null
            return (
              <li key={item.contentId} className="mydrafts__item">
                <div className="mydrafts__head">
                  <p className="mydrafts__title">{item.title || t.untitled}</p>
                  {isBlocked ? (
                    <span className="pill pill--stale">{t.stoppedByCheck}</span>
                  ) : (
                    <span className="pill pill--draft">{t.draft}</span>
                  )}
                </div>

                <p className="mydrafts__body">{item.body}</p>

                {/* The reason, from the verdict that stopped it. This is the
                    thing FED-16's appeal will point at, and until 12 Sep the
                    author could not read one at all. */}
                {isBlocked && item.blockReason && (
                  <p className="mydrafts__why">
                    {item.blockReason}
                    {item.blockRules.length > 0 && (
                      <>
                        {' '}
                        <span className="mono">({item.blockRules.join(', ')})</span>
                      </>
                    )}
                  </p>
                )}

                <p className="mydrafts__actions">
                  {!isBlocked && (
                    <button
                      type="button"
                      className="btn btn--primary btn--xs"
                      disabled={busy !== null}
                      onClick={() => onPick(item)}
                    >
                      {t.open}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn--quiet btn--xs"
                    disabled={busy !== null}
                    onClick={() => void throwAway(item)}
                  >
                    {busy === item.contentId
                      ? t.working
                      : item.firstPublishedAt === null
                        ? t.delete
                        : t.remove}
                  </button>
                </p>
              </li>
            )
          })}
        </ul>
      )}

      <p className="mydrafts__foot">
        <button type="button" className="btn btn--quiet" onClick={onClose}>
          {t.close}
        </button>
      </p>
    </>
  )
}
